// src/components/home/FocusAreasSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLeaf, FaLaptop, FaDraftingCompass, FaLightbulb, FaArrowRight, FaSolarPanel, FaRecycle, 
  FaRobot, FaGlobe, FaPython, FaBuilding, FaPencilRuler, FaMap, FaUserTie,
  FaChalkboardTeacher, FaBriefcase, FaHelicopter } from 'react-icons/fa';
import Section from '../common/Section';
import Button from '../common/Button';
import Heading from '../common/Heading';

const FocusAreasSection = () => {
  const [activeTab, setActiveTab] = useState('digital');
  
  const focusAreas = [
    {
      id: 'green',
      title: 'Green Skills & Climate',
      icon: <FaLeaf className="text-green-500" />,
      summary: 'The Green Skills and Climate programs aim to equip youths with the competencies needed to tap into Sierra Leone\'s vast renewable energy potential, take climate action and promote environmental sustainability for green growth.',
      programs: [
        {
          name: 'Solar Systems Design and Installation',
          icon: <FaSolarPanel />
        },
        {
          name: 'Waste to Value',
          icon: <FaRecycle />
        }
      ]
    },
    {
      id: 'digital',
      title: 'Digital Literacy',
      icon: <FaLaptop className="text-blue-500" />,
      summary: 'These programs are designed to build youths\' basic and advanced digital technology capacity as a powerful tool for diversified and inclusive economic development.',
      programs: [
        {
          name: 'Introduction to AI literacy',
          icon: <FaRobot />
        },
        {
          name: 'Web Design',
          icon: <FaGlobe />
        },
        {
          name: 'Graphics Design',
          icon: <FaPencilRuler />
        },
        {
          name: 'Microsoft Office',
          icon: <FaLaptop />
        },
        {
          name: 'Digital Marketing and Social Media',
          icon: <FaGlobe />
        },
        {
          name: 'Python Programming',
          icon: <FaPython />
        }
      ]
    },
    {
      id: 'engineering',
      title: 'Engineering & Design',
      icon: <FaDraftingCompass className="text-purple-500" />,
      summary: 'These courses aim to build engineers\' and students\' skills in emerging design and modelling technologies and tools, which will increase their productivity and competitiveness in local and global job markets.',
      programs: [
        {
          name: 'Good Drones for SaLone',
          icon: <FaHelicopter />,
          tracks: [
            'Drones for Land Survey',
            'Drones for Disaster Response and Recovery',
            'Drones for Multimedia Production'
          ]
        },
        {
          name: 'Building Information Modelling',
          icon: <FaBuilding />,
          subprograms: [
            'Revit Architecture',
            'Chief Architect',
            'Bill of Quantities',
            'Robot Structural Analysis'
          ]
        },
        {
          name: 'Computer-Aided Design',
          icon: <FaDraftingCompass />,
          subprograms: [
            'AutoCAD',
            'Design for 3D printing & fabrication (Fusion360)'
          ]
        },
        {
          name: 'Geospatial',
          icon: <FaMap />,
          subprograms: [
            'Land Survey and Mapping',
            'Geographical Information Systems'
          ]
        }
      ]
    },
    {
      id: 'entrepreneurship',
      title: 'Entrepreneurship and Mentorship',
      icon: <FaLightbulb className="text-yellow-500" />,
      summary: 'These programs are targeted events, workshops, conferences, and seminars designed to deliver practical coaching to trainees on how to commercialise their technical skills, hunt opportunities, penetrate industries and build thriving start-ups.',
      programs: [
        {
          name: 'Startup workshop',
          icon: <FaLightbulb />
        },
        {
          name: 'Career garage for young professionals',
          icon: <FaChalkboardTeacher />
        },
        {
          name: 'Industry placement for youths',
          icon: <FaBriefcase />
        }
      ]
    }
  ];
  
  const currentFocusArea = focusAreas.find(area => area.id === activeTab);
  
  return (
    <Section background="white" padding="normal" className="border-b border-gray-200">
      {/* Added Section Title */}
      <div className="text-center mb-8">
        <Heading level={2} withAccent align="center">
          Skills Training Categories
        </Heading>
        <p className="text-gray-600 max-w-3xl mx-auto mt-4">
          Explore our comprehensive range of skill development programs designed to empower youth and drive sustainable economic growth in Sierra Leone.
        </p>
      </div>
      
      <div className="bg-primary rounded-xl overflow-hidden shadow-xl">
        {/* Tabs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {focusAreas.map((area) => (
            <button
              key={area.id}
              onClick={() => setActiveTab(area.id)}
              className={`py-4 px-6 text-center transition-all duration-300 ${
                activeTab === area.id
                  ? 'bg-white text-primary font-semibold'
                  : 'bg-primary text-white hover:bg-primary-dark'
              }`}
            >
              <span className="text-sm lg:text-base">{area.title}</span>
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="bg-white p-6 md:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentFocusArea.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-start flex-col md:flex-row">
                <div className="md:w-2/3 pr-0 md:pr-8">
                  <div className="flex items-center mb-4">
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mr-4 shadow-md">
                      {currentFocusArea.icon}
                    </div>
                    <h3 className="text-xl font-bold text-primary-dark">{currentFocusArea.title}</h3>
                  </div>
                  
                  <p className="text-gray-700 mb-6 italic">{currentFocusArea.summary}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-primary-dark mb-4">Programs Offered:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {currentFocusArea.programs.map((program, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100">
                          <div className="flex items-center mb-2">
                            <div className="h-8 w-8 rounded-full bg-primary bg-opacity-10 flex items-center justify-center mr-3">
                              <span className="text-primary">{program.icon}</span>
                            </div>
                            <h5 className="font-semibold text-primary-dark">{program.name}</h5>
                          </div>
                          
                          {program.tracks && (
                            <div className="ml-11">
                              <p className="text-sm text-gray-600 font-medium mb-1">Tracks:</p>
                              <ul className="text-sm text-gray-700 space-y-1">
                                {program.tracks.map((track, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="text-secondary mr-2">•</span>
                                    <span>{track}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {program.subprograms && (
                            <div className="ml-11">
                              <p className="text-sm text-gray-600 font-medium mb-1">Includes:</p>
                              <ul className="text-sm text-gray-700 space-y-1">
                                {program.subprograms.map((subprogram, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="text-secondary mr-2">•</span>
                                    <span>{subprogram}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <Button
                      variant="primary"
                      to={`/programs#${currentFocusArea.id}`}
                      icon={<FaArrowRight />}
                      iconPosition="right"
                    >
                      Explore {currentFocusArea.title} Programs
                    </Button>
                  </div>
                </div>
                
                <div className="md:w-1/3 mt-6 md:mt-0">
                  <div className="bg-gray-100 rounded-lg shadow-md overflow-hidden">
                    <img 
                      src={`/images/focus-areas/${currentFocusArea.id}.jpg`} 
                      alt={currentFocusArea.title}
                      className="w-full h-auto object-cover"
                      style={{ minHeight: '250px' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://placehold.co/600x400/1a237e/ffffff?text=${currentFocusArea.title}`;
                      }}
                    />
                    <div className="p-4 bg-gray-50">
                      <h4 className="font-semibold text-primary-dark mb-2">Why Choose {currentFocusArea.title}?</h4>
                      <p className="text-sm text-gray-700">
                        Gain in-demand skills that set you apart in the job market and pave the way for entrepreneurial success.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
};

export default FocusAreasSection;